import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign, ShoppingBag, Shield, FileText, Zap, RefreshCw, Award, Users, Truck, BarChart3, Loader2, Activity, Tag } from 'lucide-react';

// Friendly labels for the first-party funnel event types (GAP-01).
const EVENT_LABELS = {
  page_view: 'Page Views',
  upload_started: 'Upload Started',
  upload_completed: 'Upload Completed',
  item_added: 'Item Added to Cart',
  addon_selected: 'Add-on Selected',
  checkout_started: 'Checkout Started',
  payment_succeeded: 'Payment Succeeded',
  payment_failed: 'Payment Failed',
  determination_set: 'Determination Set',
  refund_created: 'Refund Created',
  more_images_requested: 'More Images Requested',
  more_images_received: 'More Images Received',
};

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [revByCat, setRevByCat] = useState([]);
  const [revBySvc, setRevBySvc] = useState([]);
  const [dets, setDets] = useState(null);
  const [turnaround, setTurnaround] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [mailedDocs, setMailedDocs] = useState(null);
  const [repeatOrders, setRepeatOrders] = useState(null);
  const [events, setEvents] = useState(null);
  const [couponUsage, setCouponUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/reports/overview'),
      api.get('/admin/reports/revenue-by-category'),
      api.get('/admin/reports/revenue-by-service'),
      api.get('/admin/reports/determinations'),
      api.get('/admin/reports/turnaround'),
      api.get('/admin/reports/team-throughput'),
      api.get('/admin/reports/mailed-documents'),
      api.get('/admin/reports/repeat-orders'),
      api.get('/admin/reports/events'),
      api.get('/admin/reports/coupon-usage'),
    ]).then(([ov, rc, rs, det, ta, tp, md, ro, ev, cu]) => {
      setData(ov.data); setRevByCat(rc.data); setRevBySvc(rs.data);
      setDets(det.data); setTurnaround(ta.data); setThroughput(tp.data);
      setMailedDocs(md.data); setRepeatOrders(ro.data); setEvents(ev.data);
      setCouponUsage(cu.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div data-testid="reports-page">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Reports</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Revenue', value: fmt(data?.total_revenue_cents || 0), icon: DollarSign, color: '#0E6B5D' },
          { label: 'Orders', value: data?.total_orders || 0, icon: ShoppingBag, color: '#A36A3A' },
          { label: 'Authenticated', value: data?.authentic_count || 0, icon: Shield, color: '#0E6B5D' },
          { label: 'Certificates', value: data?.certificates_issued || 0, icon: Award, color: '#1F5E7A' },
          { label: 'Refunds', value: data?.refund_count || 0, icon: RefreshCw, color: '#5A1F2A' },
        ].map((m, i) => {
          const Icon = m.icon;
          return (
            <Card key={i} className="bg-white border-stone/50">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-3.5 w-3.5" style={{ color: m.color }} />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.label}</span>
                </div>
                <p className="text-lg font-semibold text-ink">{m.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue by Category */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Revenue by Category</h3>
            <div className="space-y-2">
              {revByCat.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-ink">{c.category}</span>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-[10px]">{c.count} items</Badge>
                    <span className="font-medium text-ink w-20 text-right">{fmt(c.revenue_cents)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Service */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Revenue by Service</h3>
            <div className="space-y-2">
              {revBySvc.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    {s.family === 'authentication' ? <Shield className="h-3.5 w-3.5" style={{color:'#0E6B5D'}} /> : <FileText className="h-3.5 w-3.5" style={{color:'#A36A3A'}} />}
                    <span className="text-ink">{s.service}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-[10px]">{s.count}</Badge>
                    <span className="font-medium text-ink w-20 text-right">{fmt(s.revenue_cents)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Determinations */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Determinations</h3>
            {dets && <div className="space-y-2 text-sm">
              {[{l:'Authentic',v:dets.authentic,c:'#0E6B5D'},{l:'Counterfeit',v:dets.counterfeit,c:'#5A1F2A'},{l:'Appraisal Complete',v:dets.appraisal_complete,c:'#A36A3A'},{l:'Unable to Determine',v:dets.unable_to_determine,c:'#6B645C'},{l:'Pending',v:dets.pending,c:'#1F5E7A'}].map((d,i)=>(
                <div key={i} className="flex items-center justify-between">
                  <span className="text-ink">{d.l}</span>
                  <span className="font-medium" style={{color:d.c}}>{d.v}</span>
                </div>
              ))}
            </div>}
          </CardContent>
        </Card>

        {/* Turnaround Times */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Turnaround Times</h3>
            {turnaround && <div className="space-y-3">
              <div><p className="text-xs text-muted-foreground">Standard</p><p className="text-lg font-semibold text-ink">{turnaround.standard_avg_hours}h avg</p><p className="text-[10px] text-muted-foreground">{turnaround.standard_count} items</p></div>
              <div><p className="text-xs text-muted-foreground">Expedited</p><p className="text-lg font-semibold text-ink">{turnaround.expedited_avg_hours}h avg</p><p className="text-[10px] text-muted-foreground">{turnaround.expedited_count} items</p></div>
            </div>}
          </CardContent>
        </Card>

        {/* Mailed Documents */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Truck className="h-3 w-3" /> Mailed Documents</h3>
            {mailedDocs && <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span>Ready to Print</span><span className="font-medium">{mailedDocs.ready_to_print}</span></div>
              <div className="flex justify-between"><span>Shipped</span><span className="font-medium">{mailedDocs.shipped}</span></div>
              <div className="flex justify-between"><span>Cancelled</span><span className="font-medium">{mailedDocs.cancelled}</span></div>
              <div className="flex justify-between"><span>Fees Refunded</span><span className="font-medium">{mailedDocs.total_fee_refunded}</span></div>
            </div>}
          </CardContent>
        </Card>

        {/* Team Throughput */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Users className="h-3 w-3" /> Team Throughput (30d)</h3>
            {throughput && <div>
              <p className="text-lg font-semibold text-ink">{throughput.total} completed</p>
              <p className="text-xs text-muted-foreground">{throughput.daily?.length || 0} active days</p>
              {throughput.daily?.slice(-7).map((d, i) => (
                <div key={i} className="flex justify-between text-xs mt-1"><span className="text-muted-foreground">{d.date}</span><span className="font-medium">{d.count}</span></div>
              ))}
            </div>}
          </CardContent>
        </Card>

        {/* Repeat Orders */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Repeat Orders</h3>
            {repeatOrders && <div>
              <p className="text-lg font-semibold text-ink">{repeatOrders.repeat_customers} repeat</p>
              <p className="text-xs text-muted-foreground">of {repeatOrders.total_customers} total customers</p>
            </div>}
          </CardContent>
        </Card>

        {/* Expedited Volume */}
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Zap className="h-3 w-3" style={{color:'#A36A3A'}} /> Expedited</h3>
            <p className="text-lg font-semibold text-ink">{data?.expedited_count || 0}</p>
            <p className="text-xs text-muted-foreground">expedited items</p>
          </CardContent>
        </Card>

        {/* First-party event funnel (GAP-01) — independent of GA4/Meta */}
        <Card className="bg-white border-stone/50 lg:col-span-2" data-testid="events-report">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
              <Activity className="h-3 w-3" style={{color:'#1F5E7A'}} /> First-Party Events
              <Badge variant="outline" className="text-[10px] ml-1">{events?.total_events || 0} total</Badge>
            </h3>
            {events && (events.by_type || []).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                {events.by_type.map((e, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                    <span className="font-medium text-ink">{e.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No events recorded yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Coupon Usage */}
        <Card className="bg-white border-stone/50 lg:col-span-2" data-testid="coupon-usage-report">
          <CardContent className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
              <Tag className="h-3 w-3" style={{ color: '#A36A3A' }} /> Coupon Usage
              <Badge variant="outline" className="text-[10px] ml-1">{couponUsage.length} coupons</Badge>
            </h3>
            {couponUsage.length === 0 ? (
              <p className="text-xs text-muted-foreground">No coupons created yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {couponUsage
                  .slice()
                  .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                  .map((c, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-stone/20 py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-ink truncate">{c.code}</span>
                      {c.is_active ? (
                        <Badge variant="outline" className="text-[9px] text-emerald-700 border-emerald-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] text-muted-foreground">Inactive</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[10px] text-muted-foreground">
                        {c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${((c.discount_value || 0) / 100).toFixed(0)}`}
                      </span>
                      <span className="font-medium text-ink w-16 text-right">{c.usage_count || 0} uses</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
